import { isPlatformServer } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { URLS } from '../../../data/navigation/navigation.data';
import { findStoreListing, isFreeSku, storeAppListPrice, storeItemName } from '../../../data/product/product-lookup.data';
import { Product, platformListPrice } from '../../../model/product/product.model';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { CartItem, CartService, MAX_QUANTITY } from '../../../service/cart/cart.service';
import { StoreGuide, StoreGuidesComponent } from '../../../component/store/store-guides/store-guides.component';
import { StoreCatalog, StoreCatalogItem, StoreService } from '../../../service/store/store.service';
import { ToastService } from '../../../service/toast/toast.service';

/** one cart row, joined from the cart (sku + quantity), the catalogue (price) and the theme data (art). */
export interface CartLine {
  sku: string;
  item: CartItem;
  product: Product | null;
  category: string;
  productName: string;
  /** "Ocean Theme for Sublime Text" */
  title: string;
  platformKey: string;
  platformName: string;
  platformLogo: string;
  /** a theme's color palette, not a theme port (the card names it a palette and puts a brush on it) */
  palette: boolean;
  /** an app (2026-10-14), not a theme: the card keeps its name and links to its page */
  app: boolean;
  appUrl: string;
  quantity: number;
  unitPriceLabel: string | null;
  linePriceLabel: string | null;
  unitAmount: number | null;
  currency: string;
  includes: string[];
  version: string;
  /** false when the catalogue no longer lists the sku (removed / renamed) */
  available: boolean;
  /** false when the sku exists but cannot be bought right now (no Stripe price yet) */
  purchasable: boolean;
  /** a free listing (2026-09-21): one copy, no price; the cart of free listings only is a free order */
  free: boolean;
}

/** what the checkout card does, from what the cart holds (owner, 2026-09-21) */
export type CartMode = 'paid' | 'free' | 'mixed';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** after handing the browser to Stripe, the Checkout button comes back if the page is somehow still here */
const LEAVE_RESET_MS = 10 * 1000;
/** after a Resend the button reads "Sent again" this long before it can be pressed once more */
const RESEND_AGAIN_MS = 60 * 1000;
/** a removed row fades out first, then leaves the cart (owner, 2026-09-20: no vanishing) */
const ROW_FADE_MS = 260;

/**
 * /cart - the lines a visitor picked (Platform.sku listings) as theme cards
 * in the site's own poster voice, each with its quantity, what it includes and
 * how to install it; the order summary; the hand-off to Stripe Checkout (see
 * base-themes/STORE-DESIGN.md §3.2). No accounts: the cart is localStorage,
 * the order is a secret link after payment.
 */
@Component( {
  selector: 'app-cart-page',
  templateUrl: './cart-page.component.html',
  styleUrls: [ './cart-page.component.scss' ],
} )
export class CartPageComponent implements OnInit, OnDestroy {

  URLS = URLS;
  MAX_QUANTITY = MAX_QUANTITY;
  /** the loading state shows as many silhouettes as the cart has room for */
  readonly SKELETON_ROWS = [ 1, 2, 3 ];

  lines: CartLine[] = [];
  /** the cart has been read (localStorage, on the browser) */
  itemsKnown = false;
  /** the catalogue request answered at least once (before that: prices are loading, not missing) */
  catalogLoaded = false;
  /**
   * Skeleton instead of rows: on the server, until the cart is read, and
   * until the store has answered (so the cart never shows "empty" before
   * the loading is done). An empty cart waits for the store at most a
   * second; it has nothing to price, so it need not wait out a slow network.
   */
  get loading(): boolean {
    if ( this.isServer || !this.itemsKnown ) return true;
    if ( this.catalogLoaded ) return false;
    return this.itemCount > 0 || !this.emptyGraceElapsed;
  }
  private emptyGraceElapsed = false;
  /** the catalogue was served by the store API (only then can a sku be "no longer available") */
  catalogOk = false;
  /** the store API could not be reached: list prices show, checkout waits, the service keeps retrying */
  get catalogFailed(): boolean {
    return this.catalogLoaded && !this.catalogOk;
  }
  /** the store has confirmed every line and nothing is in flight */
  get linesReady(): boolean {
    return this.catalogOk && this.lines.length > 0 && !this.checkingOut && this.lines.every( line => line.available && line.purchasable );
  }

  /** every line has a live price and can be bought; a free cart also needs the address */
  get checkoutReady(): boolean {
    return this.linesReady && ( this.mode !== 'free' || this.emailValid );
  }

  /** the button: a free cart keeps it live so a click can point at the missing address */
  get ctaDisabled(): boolean {
    return !this.linesReady;
  }

  /**
   * paid = every line has a price (Stripe Checkout), free = every line is free
   * (the order is made here, against an e-mail; "Download free"), mixed = both
   * (Stripe lists the paid lines and names the free ones; the order holds all)
   */
  get mode(): CartMode {
    const free = this.lines.filter( line => line.free ).length;
    if ( free === 0 ) return 'paid';
    return free === this.lines.length ? 'free' : 'mixed';
  }

  get freeCount(): number {
    return this.lines.filter( line => line.free ).length;
  }

  /** the free flow's address (the downloads mail goes there) and the opt-in to news */
  email = '';
  // unticked by default: an EU buyer's marketing consent has to be an active tick (owner, 2026-09-21)
  marketingConsent = false;
  emailTouched = false;

  get emailValid(): boolean {
    const value = this.email.trim();
    return EMAIL_PATTERN.test( value ) && value.length <= 320;
  }

  itemCount = 0;
  /** the skus in the cart, for the suggestions strip (a new array only when the lines change) */
  lineSkus: string[] = [];
  /** Getting started, one box per platform in the cart (under the panel) */
  guides: StoreGuide[] = [];
  subtotalLabel: string | null = null;
  /** the Checkout button is disabled while the session is created and until the browser leaves for Stripe */
  checkingOut = false;
  checkoutError: string | null = null;

  private subs: Subscription[] = [];
  private leaveTimer: any = null;
  private readonly isServer: boolean;

  constructor(
    @Inject( PLATFORM_ID ) platformId: Object,
    private route: ActivatedRoute,
    private router: Router,
    private cartService: CartService,
    private storeService: StoreService,
    private toastService: ToastService,
    private analyticsService: AnalyticsService,
  ) {
    this.isServer = isPlatformServer( platformId );
  }

  ngOnInit(): void {
    if ( !this.isServer ) {
      setTimeout( () => this.emptyGraceElapsed = true, 1000 );
    }
    this.subs.push(
      // the cart itself is known at once (localStorage); the prices follow
      this.cartService.items$.subscribe( items => {
        this.itemsKnown = true;
        this.itemCount = items.reduce( ( n, item ) => n + item.quantity, 0 );
      } ),
      combineLatest( [ this.cartService.items$, this.storeService.catalog$ ] ).subscribe( ( [ items, catalog ] ) => {
        this.catalogLoaded = true;
        this.catalogOk = catalog.ok;
        const bySku = this.storeService.indexBySku( catalog );
        this.lines = items.map( item => this.buildLine( item, bySku[ item.sku ], catalog ) );
        const skus = this.lines.map( l => l.sku );
        if ( skus.join( '|' ) !== this.lineSkus.join( '|' ) ) this.lineSkus = skus;
        this.guides = StoreGuidesComponent.guidesFor( this.lines.map( l => ( { platform: l.platformKey, platformName: l.platformName, platformLogo: l.platformLogo } ) ) );
        this.itemCount = items.reduce( ( n, item ) => n + item.quantity, 0 );
        this.subtotalLabel = this.computeSubtotal();
      } ),
      this.route.queryParams.subscribe( params => {
        if ( params.checkout === 'cancelled' ) {
          this.toastService.show( 'Checkout cancelled. Your cart is still here.' );
          this.router.navigate( [], { relativeTo: this.route, queryParams: {}, replaceUrl: true } );
        }
      } ),
    );

    this.analyticsService.logEvent( 'Cart Viewed', this.cartService.analyticsProps() );
  }

  ngOnDestroy(): void {
    this.subs.forEach( sub => sub.unsubscribe() );
    this.clearLeaveTimer();
    clearTimeout( this.resendTimer );
  }

  /**
   * Back from Stripe: the browser restores this page from its back-forward
   * cache with the state it left with, so the button would stay disabled.
   */
  @HostListener( 'window:pageshow', [ '$event' ] )
  onPageShow( event: PageTransitionEvent ): void {
    if ( event.persisted ) this.resetCheckout();
  }

  changeQuantity( line: CartLine, delta: number ): void {
    const next = line.quantity + delta;
    if ( next <= 0 ) {
      this.remove( line );
      return;
    }
    this.cartService.setQuantity( line.sku, next, 'cart' );
  }

  /** rows on their way out (faded, then removed) */
  readonly leavingSkus = new Set<string>();

  remove( line: CartLine ): void {
    if ( this.leavingSkus.has( line.sku ) ) return;
    this.leavingSkus.add( line.sku );
    setTimeout( () => {
      this.leavingSkus.delete( line.sku );
      this.cartService.remove( line.sku, 'cart' );
      this.toastService.show( `${ line.title } removed`, {
        actionLabel: 'Undo',
        action: () => this.cartService.restore( line.item ),
        duration: 6000,
      } );
    }, ROW_FADE_MS );
  }

  /**
   * Checkout: the Stripe Checkout Session for a cart with paid lines (the
   * browser leaves for Stripe), or the free order for a cart of free listings
   * only (made at once against the address; the browser goes to the order
   * page, the cart is emptied).
   */
  checkout(): void {
    if ( this.mode === 'free' ) this.emailTouched = true;
    if ( !this.checkoutReady ) return;
    this.checkingOut = true;
    this.checkoutError = null;
    const items = this.lines.map( line => ( { sku: line.sku, quantity: line.quantity } ) );
    const free = this.mode === 'free';

    this.analyticsService.logEvent( 'Checkout Started', { ...this.cartService.analyticsProps(), subtotal: this.subtotalLabel, mode: this.mode } );

    // the browser's Mixpanel id travels with the order, so the server's Purchase / Free Order
    // events land on the same person as this visit (never blocks the checkout: null on failure)
    this.analyticsService.distinctId().then( ( analyticsId ) => this.storeService.checkout( items, free ? this.email.trim() : undefined, free ? this.marketingConsent : undefined, analyticsId ).subscribe( {
      next: ( response ) => {
        if ( response?.free ) {
          // the free order is made and its downloads mail is on its way: the cart is done, and the
          // panel says where the link went (owner, 2026-09-21: the order page opens from that mail
          // only, so the address has to be a real one)
          this.analyticsService.logEvent( 'Free Order Created', { ...this.cartService.analyticsProps(), orderNumber: response.orderNumber } );
          this.cartService.clear();
          this.sent = { email: response.email || this.email.trim(), orderNumber: response.orderNumber };
          this.checkingOut = false;
          window.scrollTo( { top: 0, behavior: 'smooth' } );
          return;
        }
        if ( response?.url ) {
          window.location.assign( response.url );
          // the page is normally gone by then; if the browser stays here (a stopped navigation, a bfcache
          // restore that fired no pageshow), the button comes back rather than staying stuck
          this.clearLeaveTimer();
          this.leaveTimer = setTimeout( () => this.resetCheckout(), LEAVE_RESET_MS );
          return;
        }
        this.checkingOut = false;
        this.checkoutError = 'Checkout could not be started. Please try again.';
      },
      error: ( err ) => {
        this.checkingOut = false;
        this.checkoutError = err?.error?.message || 'Checkout is unavailable right now. Please try again in a minute.';
      },
    } ) );
  }

  // ---------------------------------------------------------------- free order sent

  /** a free order was just made: the panel shows where its downloads mail went instead of the (now empty) cart */
  sent: { email: string; orderNumber: string } | null = null;
  resending = false;
  resent = false;
  private resendTimer: any = null;

  /** mails the address its order links again (the server answers ok either way and keeps its own cooldown) */
  resend(): void {
    if ( !this.sent || this.resending || this.resent ) return;
    this.resending = true;
    this.analyticsService.logEvent( 'Free Order Mail Resent', { orderNumber: this.sent.orderNumber } );
    this.storeService.findOrder( this.sent.email ).subscribe( {
      next: () => this.markResent(),
      error: () => this.markResent(),
    } );
  }

  private markResent(): void {
    this.resending = false;
    this.resent = true;
    clearTimeout( this.resendTimer );
    this.resendTimer = setTimeout( () => this.resent = false, RESEND_AGAIN_MS );
  }

  /** back to an empty cart (the sent panel is a one-off) */
  dismissSent(): void {
    this.sent = null;
    this.resent = false;
    this.email = '';
    this.emailTouched = false;
    this.marketingConsent = false;
  }

  private resetCheckout(): void {
    this.clearLeaveTimer();
    this.checkingOut = false;
  }

  private clearLeaveTimer(): void {
    if ( this.leaveTimer ) {
      clearTimeout( this.leaveTimer );
      this.leaveTimer = null;
    }
  }

  trackBySku( index: number, line: CartLine ): string {
    return line.sku;
  }

  // ---------------------------------------------------------------- helpers

  private buildLine( item: CartItem, catalogItem: StoreCatalogItem | undefined, catalog: StoreCatalog ): CartLine {
    const listing = findStoreListing( item.sku );
    const product = listing?.product ?? null;
    const productKey = listing?.productKey ?? item.sku;
    const platformKey = listing?.platformKey ?? '';
    const platformName = listing?.platformName ?? '';
    // The live price from the catalogue; while the store API is unreachable
    // the listing's own list price (product.data), so the row never goes blank.
    const listPrice = catalog.ok ? undefined : ( platformListPrice( product ?? undefined, platformName ) ?? storeAppListPrice( item.sku ) );
    const unitAmount = catalogItem?.unitAmount ?? listPrice?.amount ?? null;
    const currency = catalogItem?.currency ?? listPrice?.currency ?? 'usd';
    const productName = product?.name ?? ( catalogItem?.name?.split( ' for ' )[ 0 ] || productKey );
    // "Ocean Theme", or "Ocean Palette" for a palette (2026-10-12)
    const themeName = storeItemName( listing, productName );
    const free = catalogItem ? catalogItem.free === true || catalogItem.unitAmount === 0 : isFreeSku( item.sku );

    return {
      sku: item.sku,
      item,
      product,
      category: listing?.category ?? '',
      productName,
      title: platformName ? `${ themeName } for ${ platformName }` : themeName,
      platformKey,
      platformName,
      platformLogo: listing?.platformLogo ?? '',
      palette: !!listing?.palette,
      app: !!listing?.app,
      appUrl: listing?.appUrl ?? '',
      quantity: item.quantity,
      unitPriceLabel: free ? 'Free' : this.storeService.formatPrice( unitAmount, currency ),
      linePriceLabel: free ? 'Free' : ( unitAmount === null ? null : this.storeService.formatPrice( unitAmount * item.quantity, currency ) ),
      unitAmount: free ? 0 : unitAmount,
      currency,
      includes: catalogItem?.includes ?? [],
      version: catalogItem?.version ?? '',
      // unreachable store: nothing is known to be gone, nothing can be bought yet
      available: catalog.ok ? !!catalogItem : true,
      purchasable: catalog.ok && !!catalogItem?.purchasable,
      free,
    };
  }

  /** the paid lines' total; "Free" when every line is free */
  private computeSubtotal(): string | null {
    if ( this.lines.length === 0 ) return null;
    if ( this.lines.every( line => line.free ) ) return 'Free';
    let total = 0;
    let currency = 'usd';
    for ( const line of this.lines ) {
      if ( line.free ) continue;
      if ( line.unitAmount === null ) return null;
      total += line.unitAmount * line.quantity;
      currency = line.currency;
    }
    return this.storeService.formatPrice( total, currency );
  }
}
